import React from 'react';
import { Edit, X } from 'lucide-react';
import { Badge, Select, IconButton } from '@anyemp/ui-kit';
import { getStatusStyles } from '../../utils/colorUtils';

/**
 * StatusSelect Component
 *
 * A reusable component for displaying and editing status with inline edit mode.
 * Features:
 * - Read-only mode: Shows status badge with edit button
 * - Edit mode: Shows select dropdown with cancel button
 * - Supports loading states during status updates
 * - Dark mode compatible
 *
 * @param {Object} props
 * @param {Object|null} props.currentStatus - Current status object with id, name, and color
 * @param {Array} props.statusOptions - Array of available status options
 * @param {boolean} props.isEditing - Whether the component is in edit mode
 * @param {boolean} props.isLoading - Whether a status update is in progress
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onCancel - Callback when cancel button is clicked
 * @param {Function} props.onChange - Callback when status is changed (receives statusId)
 * @param {string} props.emptyLabel - Label to show when no status is set (default: "Not defined")
 * @param {string} props.selectPlaceholder - Placeholder for select dropdown (default: "Select status")
 */
const StatusSelect = ({
  currentStatus,
  statusOptions = [],
  isEditing = false,
  isLoading = false,
  onEdit,
  onCancel,
  onChange,
  emptyLabel = "Not defined",
  selectPlaceholder = "Select status"
}) => {
  // Read-only mode: Display status badge with edit button
  if (!isEditing) {
    return (
      <div className="flex items-center space-x-2">
        {currentStatus ? (
          <Badge
            label={currentStatus.name}
            variant="subtle"
            size="small"
            style={getStatusStyles(currentStatus.color, {
              backgroundColorOpacity: 0.15,
              borderOpacity: 0.4,
              textOpacity: 1
            })}
          />
        ) : (
          <Badge label={emptyLabel} variant="subtle" size="small" color="neutral" />
        )}
        {onEdit && (
          <IconButton
            variant="ghost"
            color="neutral"
            size="small"
            aria-label="Edit status"
            title="Edit status"
            onClick={onEdit}
            icon={<Edit className="h-3 w-3" />}
          />
        )}
      </div>
    );
  }

  // Edit mode: Display select dropdown with cancel button
  return (
    <div className="flex w-full min-w-0 items-center gap-2 sm:min-w-[180px]">
      <Select
        value={currentStatus?.id ? String(currentStatus.id) : null}
        onChange={(v) => onChange(v)}
        disabled={isLoading}
        size="small"
        fullWidth
        placeholder={selectPlaceholder}
        options={statusOptions.map((status) => ({ value: String(status.id), label: status.name }))}
      />
      {onCancel && (
        <IconButton
          variant="ghost"
          color="error"
          size="small"
          aria-label="Cancel"
          title="Cancel"
          disabled={isLoading}
          onClick={onCancel}
          icon={<X className="h-4 w-4" />}
        />
      )}
    </div>
  );
};

export default StatusSelect;
